import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  Box,
  Divider,
  List,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { palette } from 'themeV5/palette';

import { useGlobalApp } from 'hooks';

import Items from '../SideBarMenu/Items';
import MenuBottomSection from '../SideBarMenu/MenuBottomSection';
import { useMenuActions } from '../SideBarMenu/hooks/useMenuActions';
import { getTitleFromPath } from '../Header/helpers';
import { useBackButton } from '../Header/hooks/useBackButton';
import MobileLogo from './MobileLogo';

import { styles } from './styles';

const MobileHeader = () => {
  const location = useLocation();
  const { role } = useGlobalApp();
  const { returnPath, handleLogOut } = useMenuActions();
  const { showBack, backUrl } = useBackButton();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [title, setTitle] = useState('');

  useEffect(() => {
    const newTitle = getTitleFromPath(location.pathname, role);
    setTitle(newTitle);
  }, [location.pathname, role]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: palette.dark.main,
          boxShadow: 'none',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            minHeight: '56px !important',
            padding: '0px 16px !important',
          }}
        >
          {/* Left Section: Menu Icon (always visible) and Back Icon (when needed) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: showBack ? '4px' : 0,
            }}
          >
            {/* Menu Icon - Always visible */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{
                padding: '8px',
                '& svg': {
                  fontSize: '24px',
                },
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Back Icon - Visible when showBack is true */}
            {showBack && (
              <IconButton
                component={Link}
                to={backUrl}
                color="inherit"
                aria-label="back"
                sx={{
                  padding: '8px',
                  '& svg': {
                    fontSize: '20px',
                  },
                }}
              >
                <ArrowBackIosIcon />
              </IconButton>
            )}
          </Box>

          {/* Title */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              px: 1,
            }}
          >
            {title}
          </Typography>

          {/* Right Section: Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginLeft: '8px',
              flexShrink: 0,
              minWidth: '48px',
            }}
          >
            <MobileLogo />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: palette.dark.main,
            color: 'white',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            position: 'relative',
          }}
        >
          {/* Close Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={handleDrawerClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

          {/* Menu Items */}
          <List sx={styles.list}>
            <Items
              open={true}
              role={role}
              closeDrawer={handleDrawerClose}
              returnPath={returnPath}
            />
          </List>

          {/* Bottom Section */}
          <MenuBottomSection
            open={true}
            onLogOut={handleLogOut}
            variant="mobile"
            borderTopStyle={{
              borderTop: '1px solid rgba(255, 255, 255, 0.12)',
            }}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default MobileHeader;
